import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * The one look every text-like control shares — `Input`, `Textarea` and
 * `SelectTrigger` all apply it, so fields stay identical across the app.
 * Don't restyle a field at the call site; change it here (DESIGN_SYSTEM.md §5).
 *
 * At rest  a soft fill (`bg-field/70`) with a faint edge, so the field reads
 *          as a recess in the surface rather than an outlined box.
 * Hover /
 * focus    the fill goes solid and focus adds a ring plus a 4px halo.
 * Invalid  (`aria-invalid`) red edge and halo — paired with a message, never
 *          colour alone.
 * Disabled a borderless muted fill, NOT 50% opacity, so a locked value stays
 *          readable instead of turning to grey mush.
 */
export const fieldClass = cn(
  "flex h-10 w-full rounded-md px-3 py-2 text-sm",
  "border border-input/50 bg-field/70",
  "placeholder:text-muted-foreground",
  "transition-colors duration-fast ease-standard",
  "hover:bg-field hover:border-input",
  "focus-visible:outline-none focus-visible:bg-field focus-visible:border-ring focus-visible:ring-4 focus-visible:ring-ring/15",
  "aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-4 aria-[invalid=true]:ring-destructive/15",
  "disabled:cursor-not-allowed disabled:border-transparent disabled:bg-muted disabled:text-muted-foreground"
);

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          fieldClass,
          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };
